import { useEffect } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useToast } from 'react-native-toast-notifications'
import { useTranslation } from 'react-i18next'
import { shouldUseNativeSupportChat } from './supportFlag'
import { osSupportBannerAtom } from './osSupportBanner'
import {
  supportChatOpenAtom,
  supportChatOpenRequestAtom,
  supportLatestUnreadAtom,
  supportToastSeenIdAtom,
} from './supportUnread'
import type { SupportUnreadPreview } from './types'

/**
 * ATL-1343 — announce a proactive support message with its text.
 *
 * Until now the only thing that changed when Atlas wrote first (the ATL-1289 in-app welcome, an
 * agent reply) was a red number on the floating support button. A count appearing on a button the
 * merchant was not looking at is not a notification, and the proactive message is precisely the one
 * we most need read.
 */

/** How long the toast stays up. Longer than an action toast — it carries text to read. */
export const SUPPORT_TOAST_DURATION_MS = 6000

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * Whether a preview transition should raise a toast.
 *
 * Pure, so the rule can be enumerated cheaply. It is NOT the whole story, and the tests do not
 * treat it as such: this function says what two values mean, never WHEN the hook first compares
 * them — and it was the mount-order half that broke (see the rendered tests in
 * `__tests__/supportMessageToast.test.js`).
 *
 * - `previous === undefined` — the server has not been observed yet this session. The first value
 *   is a BASELINE, never a toast: it can legitimately be a three-day-old unread message that was
 *   already announced by push while the app was closed, and re-announcing it at every cold start is
 *   noise rather than notification.
 * - `previous === id` — the same message. `getUnread` is refetched on every realtime signal and
 *   returns a fresh object each time, so identity comparison would re-toast indefinitely.
 * - `id === null` — nothing unread any more (the merchant read the thread). Nothing to announce.
 */
export const shouldShowSupportToast = (
  previous: string | null | undefined,
  next: SupportUnreadPreview | null
): boolean => {
  const id = next?.messageId ?? null
  if (previous === id) return false
  if (previous === undefined) return false
  return id !== null
}

/**
 * The toast's text: who it is from, then what it says.
 *
 * A message with no body is an attachment-only one — announcing it as an empty line would read as a
 * broken toast, so it says that a file arrived.
 */
export const buildSupportToastText = (preview: SupportUnreadPreview, t: Translate): string => {
  const headline = preview.agentName
    ? t('support.newMessageFrom', { name: preview.agentName })
    : t('support.newMessage')
  const body = preview.preview || (preview.hasAttachments ? t('support.newMessageAttachment') : '')
  return body ? `${headline}\n${body}` : headline
}

/**
 * Show the toast when a new unread support message appears. Mount once, alongside
 * `useSupportUnreadWatcher` — it is that watcher which refreshes `supportLatestUnreadAtom`.
 *
 * Tapping it sets `supportChatOpenRequestAtom`: the same seam a push-notification tap uses, already
 * honoured by `FloatingButtonWithModal`. So the toast cannot reach a chat the push path could not.
 */
export const useSupportMessageToast = (): void => {
  const latest = useAtomValue(supportLatestUnreadAtom)
  const [seenId, setSeenId] = useAtom(supportToastSeenIdAtom)
  const chatOpen = useAtomValue(supportChatOpenAtom)
  const setChatOpenRequest = useSetAtom(supportChatOpenRequestAtom)
  // ATL-1345: whether the OS will show its own banner for this reply. Always `not-shown` on web.
  const osBanner = useAtomValue(osSupportBannerAtom)
  const toast = useToast()
  const { t } = useTranslation()

  useEffect(() => {
    // `undefined` is "the server has not answered yet", which is not an observation and must not
    // be spent as the baseline: this effect runs once at mount, before the watcher's first
    // `getUnread` resolves, and an atom that started at `null` would leave the marker at `null`
    // here — so the first response of a cold start would read as a CHANGE and toast a message
    // that may be days old. Bail out until the server has actually reported something.
    if (latest === undefined) return
    const id = latest?.messageId ?? null
    if (seenId === id) return
    // The baseline is a fact about the SERVER, not about the banner, so it is spent here whatever
    // the banner state. Were it held back with the announcement below, a brand-new merchant on
    // native — first answer `null`, welcome ~60s later, both landing while the OS permission
    // dialog is still up — would have the welcome become the baseline once they tapped
    // "Don't Allow", and be told nothing by either channel.
    if (seenId === undefined) {
      setSeenId(id)
      return
    }
    // Native only (web starts at `not-shown`): a REAL change, but the push prompt has not yet said
    // whether a banner is coming. Hold it — the marker is not advanced, so when the verdict lands
    // this effect runs again and decides. Declining now would lose the message for good.
    if (osBanner === 'unknown') return
    const show = shouldShowSupportToast(seenId, latest)
    // Advance the marker whatever happens next. Every path below that declines to toast declines
    // because the message should not be announced NOW — none of them should leave it queued to
    // surprise the merchant later.
    setSeenId(id)
    if (!show || !latest) return
    // The chat panel is on screen: the merchant is already reading this message.
    if (chatOpen) return
    // Same kill switch as the badge — with native chat off there is no chat for a tap to open.
    if (!shouldUseNativeSupportChat()) return
    // ATL-1345 (PM decision): push is on, so the OS foreground banner already carries this preview
    // and already opens the chat on tap. A toast would announce the same text twice. The banner is
    // deliberately NOT the one suppressed — see `osSupportBannerRule.ts`.
    if (osBanner === 'shown') return
    toast.show(buildSupportToastText(latest, t), {
      type: 'normal',
      // Top, unlike the app's outcome toasts ("sale saved", print failures), which sit at the
      // bottom: this one is an interruption from elsewhere, it must not cover the floating support
      // button it is telling the merchant to open, and it must not be mistaken for the result of
      // whatever they just tapped.
      placement: 'top',
      duration: SUPPORT_TOAST_DURATION_MS,
      animationType: 'slide-in',
      onPress: () => setChatOpenRequest(true),
    })
  }, [latest, seenId, setSeenId, chatOpen, osBanner, setChatOpenRequest, toast, t])
}
